/**
 * Pure resolver for the ASME B31.3 §304.1.1 pressure-design coefficient Y. No React, no
 * `GeckoMeasurement` — callers convert to working units (mm) first
 * (`toNumber(WORKING_UNIT_THICKNESS)`).
 *
 * Two branches, selected by wall thickness vs `D/6`:
 *  - `t < D/6`  → Table 304.1.1 lookup (the caller renders the grid; the user picks a cell).
 *  - `t >= D/6` → the §304.1.1 formula `Y = (d + 2c) / (D + d + 2c)` with `d = D - 2t` (inside
 *    diameter) and `c` = the corrosion/mechanical allowance (0 in the asset-model editor, which
 *    has no allowance field).
 */

export type YResolution =
  | { state: 'unavailable' }
  | { state: 'table' }
  | { state: 'formula'; y: number; D: number; d: number; t: number; c: number }

export interface ResolveArgs {
  /** Wall thickness used for the branch + formula, mm. */
  tMm: number | null
  /** Outside diameter, mm. */
  dMm: number | null
  /** Corrosion/mechanical allowance c, mm. Defaults to 0 (no allowance field in the editor). */
  cMm?: number
}

export function resolvePressureCoefficient({
  tMm,
  dMm,
  cMm = 0,
}: ResolveArgs): YResolution {
  // Geometry must be present and physical: D > 0 and 0 <= t < D/2. (After this guard TypeScript
  // narrows tMm/dMm to `number`, so the formula branch needs no casts.)
  if (dMm === null || dMm <= 0 || tMm === null || tMm < 0 || tMm >= dMm / 2) {
    return { state: 'unavailable' }
  }
  // Thin wall → Table 304.1.1 lookup.
  if (tMm < dMm / 6) {
    return { state: 'table' }
  }
  // Thick wall (t >= D/6) → ASME B31.3 §304.1.1: Y = (d + 2c) / (D + d + 2c), d = D - 2t.
  const d = dMm - 2 * tMm
  const denom = dMm + d + 2 * cMm
  if (denom <= 0) {
    return { state: 'unavailable' }
  }
  return {
    state: 'formula',
    y: (d + 2 * cMm) / denom,
    D: dMm,
    d,
    t: tMm,
    c: cMm,
  }
}
